import {
  parseBotProfile,
  sanitizeBotFilename,
  serializeBotProfile,
  type BotProfileDocument,
} from '../../sim/botProfile/index.ts';

const STORAGE_KEY = 'battleboats:bots:draft';
const FILE_NAME_KEY = 'battleboats:bots:file';
const DIRTY_KEY = 'battleboats:bots:dirty';
const DIRTY_VALUE = '1';
const REPO_SAVE_PATH = '/__battleboats/save-bot';
const REPO_DELETE_PATH = '/__battleboats/delete-bot';

export type ResumableBotDraft = {
  profile: BotProfileDocument;
  fileName: string | null;
};

export function saveBotDraft(profile: BotProfileDocument, fileName: string | null): void {
  try {
    localStorage.setItem(STORAGE_KEY, serializeBotProfile(profile));
    localStorage.setItem(DIRTY_KEY, DIRTY_VALUE);

    if (fileName) {
      localStorage.setItem(FILE_NAME_KEY, fileName);
    } else {
      localStorage.removeItem(FILE_NAME_KEY);
    }
  } catch {
    // Ignore storage failures; autosave is best-effort.
  }
}

export function clearBotDraft(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(FILE_NAME_KEY);
    localStorage.removeItem(DIRTY_KEY);
  } catch {
    // Ignore storage failures; autosave is best-effort.
  }
}

export function loadBotDraft(): ResumableBotDraft | null {
  try {
    if (localStorage.getItem(DIRTY_KEY) !== DIRTY_VALUE) {
      return null;
    }

    const raw = localStorage.getItem(STORAGE_KEY);
    const profile = raw ? parseBotProfile(raw) : null;

    if (!profile) {
      return null;
    }

    const stored = localStorage.getItem(FILE_NAME_KEY);
    return { profile, fileName: stored ? sanitizeBotFilename(stored) : null };
  } catch {
    return null;
  }
}

export function canWriteRepoBot(): boolean {
  return import.meta.env.DEV;
}

export async function writeRepoBot(filename: string, contents: string): Promise<boolean> {
  const name = sanitizeBotFilename(filename);

  if (!name) {
    return false;
  }

  try {
    const response = await fetch(REPO_SAVE_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: name, contents }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function deleteRepoBot(filename: string): Promise<boolean> {
  const name = sanitizeBotFilename(filename);

  if (!name) {
    return false;
  }

  try {
    const response = await fetch(REPO_DELETE_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: name }),
    });
    return response.ok || response.status === 404;
  } catch {
    return false;
  }
}

/** Preview and production cannot write `bots/`. Offer a download instead. */
export function downloadBotFile(profile: BotProfileDocument, filename: string): void {
  const blob = new Blob([serializeBotProfile(profile)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
